import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { PageHeader, StatusBadge, ProgressBar, Modal, MetricCard } from '../components/UI';

const EMPTY = { name: '', client: '', status: 'Active', progress: 0, budget: 0, spent: 0, start: '', end: '', description: '', contractRef: '' };

export default function Projects({ setPage }) {
  const { data, activeProjectId, switchProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [form, setForm] = useState(null);

  const save = () => {
    upsert('projects', form);
    setForm(null);
  };

  const fmtM = n => '$' + (n / 1e6).toFixed(2) + 'M';

  return (
    <div className="page-content">
      <PageHeader title="Projects" subtitle={`${data.projects.length} project(s) in portfolio`}>
        {can('canCreate') && (
          <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY, id: null, projectId: null })}>+ New Project</button>
        )}
      </PageHeader>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {data.projects.map(p => {
          const isActive = p.id === activeProjectId;
          const costs = data.costs.filter(c => c.projectId === p.id);
          const totalBudget = costs.reduce((a, c) => a + c.budgeted, 0) || p.budget;
          const totalSpent  = costs.reduce((a, c) => a + c.actual, 0)   || p.spent;
          return (
            <div key={p.id} className="card" style={{ borderColor: isActive ? 'var(--accent)' : undefined, cursor: 'pointer' }}
              onClick={() => switchProject(p.id)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700 }}>{p.name}</h2>
                    <StatusBadge value={p.status} />
                    {isActive && <span className="badge badge-blue">Active</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{p.client} · {p.contractRef}</div>
                  {p.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{p.description}</div>}
                </div>
                {can('canEdit') && (
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setForm({ ...p })}>Edit</button>
                    {can('canDelete') && (
                      <button className="btn btn-danger btn-sm" onClick={() => deleteItem('projects', p.id)}>Delete</button>
                    )}
                  </div>
                )}
              </div>
              <div className="grid-4" style={{ marginBottom: 14 }}>
                <MetricCard label="Budget" value={fmtM(totalBudget)} />
                <MetricCard label="Spent" value={fmtM(totalSpent)} color="var(--amber)" />
                <MetricCard label="Remaining" value={fmtM(totalBudget - totalSpent)} color="var(--green)" />
                <MetricCard label="Schedule" value={`${p.start} → ${p.end}`} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                  <span>Overall Progress</span><span>{p.progress}%</span>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div className="progress-fill progress-blue" style={{ width: p.progress + '%', background: 'linear-gradient(90deg, var(--accent), #8b5cf6)' }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <Modal title={form.id ? 'Edit Project' : 'New Project'} onClose={() => setForm(null)}>
          <div className="form-grid">
            <div className="form-group form-full"><label>Project Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-group"><label>Client</label>
              <input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} /></div>
            <div className="form-group"><label>Contract Ref</label>
              <input value={form.contractRef} onChange={e => setForm({ ...form, contractRef: e.target.value })} /></div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['Active', 'On Hold', 'Completed', 'Cancelled'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Progress (%)</label>
              <input type="number" min="0" max="100" value={form.progress} onChange={e => setForm({ ...form, progress: +e.target.value })} /></div>
            <div className="form-group"><label>Budget (USD)</label>
              <input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: +e.target.value })} /></div>
            <div className="form-group"><label>Amount Spent (USD)</label>
              <input type="number" value={form.spent} onChange={e => setForm({ ...form, spent: +e.target.value })} /></div>
            <div className="form-group"><label>Start Date</label>
              <input type="date" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} /></div>
            <div className="form-group"><label>End Date</label>
              <input type="date" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} /></div>
            <div className="form-group form-full"><label>Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save Project</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
